// /src/observer/observerSections.ts
/**
 * observerSections.ts — GSAP/ScrollTrigger section activation
 */
import { log } from "../utils/debug";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { normalizeScrollWrapper, initHiddenState } from "../utils/normalize";
import { stopAllMedia, pauseMediaInSection, VIDEO_SELECTOR, enforceBackgroundAudioMix, computeDesiredVideoVolume, pauseAllOtherMedia } from "../utils/mediaUtils";
import { handleSectionVideo, watchForLateVideos, playSectionVideos, pauseSectionVideos } from "./videoAuto";
import { initAutoplay as initReelAutoplay } from "../elements/hudReel/hudReelAutoplay";
import { scheduleBackgroundIgnitionFor } from "./bgIgnition";
import { pauseBackgroundFor, resumeBackgroundFor } from "./bgControl";
import { fadeInSectionArrows, initArrowNavigation } from "../elements/arrow";
import { playSectionAudio } from "../utils/narrationUtils";
import { NARRATION_SELECTOR } from "@/elements/audio";
import { AudioController } from "@/utils/audioController";
import { trackStorySectionView } from "../utils/clarityAnalytics";

// GSAP-based reveal util
import { primeSectionForReveal } from "./reveal";
import { animateElements } from "../elements/animate";
import { cancelSectionAnimations } from "../elements/animate";

gsap.registerPlugin(ScrollTrigger);

// ---- debug
function dlog(...args: any[]) {
  try { if ((window as any).__SC_DEBUG_AUTOPLAY) log("[AUTO]", ...args); } catch {}
}

let lastSection: HTMLElement | null = null;
let firstReelSection: HTMLElement | null = null;

// Track per-section resources we need to clean
type Tracked = { mo?: MutationObserver | null; revealTimeout?: number | null; narrDelayTimeout?: number | null };
const tracked = new WeakMap<HTMLElement, Tracked>();


function findReelVideo(section: HTMLElement): HTMLVideoElement | null {
  return (
    section.querySelector<HTMLVideoElement>('video[data-reel="true"]') ||
    section.querySelector<HTMLVideoElement>(".video-reel video") ||
    section.querySelector<HTMLVideoElement>("video")
  );
}

function ensureTracked(section: HTMLElement): Tracked {
  let t = tracked.get(section);
  if (!t) { t = {}; tracked.set(section, t); }
  return t;
}

/* Bind reveal-on-play even if the video is injected late */
function bindReelRevealTriggers(section: HTMLElement) {
  const t = ensureTracked(section);
  const attach = (video: HTMLVideoElement) => {
    if ((video as any).__scRevealBound) return;
    (video as any).__scRevealBound = 1;

    const doRevealOnce = () => {
      if (section.dataset.reelAnimated === "1") return;
      section.dataset.reelAnimated = "1";
      try { animateElements(section); } catch (e) {
        console.warn("[OBSERVER] animateElements(reel) failed:", e);
      }
    };

    if (video.paused === false) doRevealOnce();
    else video.addEventListener("play", doRevealOnce, { once: true, passive: true });
  };

  const existing = findReelVideo(section);
  if (existing) attach(existing);

  if (t.mo) t.mo.disconnect();
  const frame = section.querySelector<HTMLElement>(".reelFrame, .reel-frame, .sc-stage") || section;
  t.mo = new MutationObserver(() => {
    const v = findReelVideo(section);
    if (v) attach(v);
  });
  t.mo.observe(frame, { childList: true, subtree: true });

  // Fallback: if no play within 2.5s, reveal text so page doesn’t look empty
  if (t.revealTimeout) clearTimeout(t.revealTimeout);
  t.revealTimeout = window.setTimeout(() => {
    if (section.dataset.reelAnimated === "1") return;
    dlog("reel-reveal-fallback");
    try { animateElements(section); } catch {}
    section.dataset.reelAnimated = "1";
  }, 2500);
}

function cleanupReelBindings(section: HTMLElement) {
  const t = tracked.get(section);
  if (!t) return;
  try { t.mo?.disconnect(); } catch {}
  t.mo = null;
  if (t.revealTimeout) { clearTimeout(t.revealTimeout); t.revealTimeout = null; }
}

/* ------------------------------ lifecycle ------------------------------- */

function onEnterSection(section: HTMLElement) {
  const idx = section.dataset.sectionIndex || "unknown";
  log(`[OBSERVER] Activating section ${idx}`);
  dlog("onEnter", { idx, vis: document.visibilityState, time: Date.now() });
  trackStorySectionView(section);

  const prev = lastSection;
  section.classList.add("visible");
  lastSection = section;

  // Infer hudType if missing (WHY: ensures audio/reveal logic is consistent)
  if (!section.dataset.hudType) {
    const hasReelVideo = !!section.querySelector('video[data-reel="true"], .video-reel, [data-reel="true"]');
    const hasNarration = !!section.querySelector("audio[data-role='narration'], audio[data-narration], audio.narration, audio[data-section-audio]");
    section.dataset.hudType = hasReelVideo ? "reel" : (hasNarration ? "narration" : "basic");
  }

  // Record the first reel when it actually becomes active.
  if (section.dataset.hudType === "reel" && !firstReelSection) {
    firstReelSection = section;
  }

  // Wake HUD immediately so play/mute controls are visible on entry.
  if (section.dataset.hudType === "reel") {
    try { document.dispatchEvent(new CustomEvent("sc:hud-wake", { detail: { section } })); } catch {}
    const hud = section.querySelector<HTMLElement>(".hud-reel-controls");
    if (hud) {
      hud.classList.add("hud-active");
      section.classList.add("hud-active");
    }
  }

  try { initHiddenState(section); } catch {}

  // Prime revealables once
  try {
    if (section.dataset.revealed !== "1") {
      primeSectionForReveal(section);
      if (section.dataset.sectionIndex === "0") section.dataset.allowFirstReveal = "1";

      if (section.dataset.hudType === "reel") {
        // Show arrows when reel finishes — listen on document (event target is document)
        const onReelEnd = (evt: Event) => {
          const det = (evt as CustomEvent<any>).detail || {};
          if (det.section === section) fadeInSectionArrows(section);
        };
        document.addEventListener("sc:reel:end", onReelEnd as EventListener, { passive: true });
        // Clean this listener when leaving
        const t = ensureTracked(section);
        (t as any).onReelEnd = onReelEnd;

        // Reveal when video starts; tolerate late injection
        bindReelRevealTriggers(section);
      } else {
        Promise
          .resolve(animateElements(section))
          .then(() => fadeInSectionArrows(section))
          .catch((e) => console.warn("[OBSERVER] animateElements(basic) failed:", e));
      }

      // Mark we’ve primed this section; actual reel reveal is guarded by reelAnimated
      section.dataset.revealed = "1";
    }
  } catch (e) {
    console.warn("[OBSERVER] reveal prime failed:", e);
  }

  // First section: kick background immediately so users see motion
  try {
    if (section.dataset.sectionIndex === "0" && section.dataset.bgIgnited !== "1") {
      (window as any).storycore?.forceBackgroundIgnition?.(section);
    }
  } catch {}

  try { resumeBackgroundFor(section); } catch {}

  // Stop media for all non-active sections; keep only this section alive.
  try {
    stopAllMedia(section);
  } catch {}

  const isNarrationSection = section.dataset.hudType === "narration";

  // Kick narration for this section once it's the active one.
  const trackedEntry = ensureTracked(section);

  try {
    const narr = section.querySelector<HTMLAudioElement>(NARRATION_SELECTOR);
    const alreadyStarted =
      section.dataset.audioStarted === "1" ||
      (narr ? narr.dataset.scPlayed === "1" : false);
    if (narr) {
      const narrMuted = isNarrationGloballyMuted();
      narr.muted = narrMuted;
      if (narrMuted) section.setAttribute("data-narration-muted", "1");
      else section.setAttribute("data-narration-muted", "0");
    }
    if (trackedEntry.narrDelayTimeout) {
      clearTimeout(trackedEntry.narrDelayTimeout);
      trackedEntry.narrDelayTimeout = null;
    }

    if (narr && !alreadyStarted) {
      trackedEntry.narrDelayTimeout = window.setTimeout(() => {
        trackedEntry.narrDelayTimeout = null;
        void playSectionAudio(narr).then((ok) => {
          if (ok) {
            section.dataset.audioStarted = "1";
            // Apply author-specified background video volume (video-audio) under narration.
            section
              .querySelectorAll<HTMLVideoElement>("video[data-role='background'], video[data-bg='true']")
              .forEach((v) => {
                try {
                  const token = v.dataset.videoAudio || v.getAttribute("data-video-audio") || "";
                  const base = computeDesiredVideoVolume(token);
                  if (base > 0) {
                    v.muted = false;
                    v.removeAttribute("muted");
                    v.volume = Math.max(0.18, Math.min(base, 0.35)); // ducked under narration
                    if (v.paused) v.play().catch(() => {});
                  } else {
                    v.muted = true;
                    v.setAttribute("muted", "");
                    v.volume = 0;
                  }
                } catch {}
              });
            enforceBackgroundAudioMix(section);
          }
        });
      }, 500);
    }
  } catch {}


  // Video setup / autoplay
  try {
    dlog("videos-init");
    const videos = section.querySelectorAll<HTMLVideoElement>(VIDEO_SELECTOR);

    videos.forEach((v) => handleSectionVideo({ section, video: v }));

    if (section.dataset.hudType === "reel") {
      dlog("reel-autoplay-via-observer", { idx: section.dataset.sectionIndex });
      videos.forEach((v) => {
      const isReelVideo =
          v.dataset.reel === "true" ||
          v.dataset.role === "reel" ||
          !!section.querySelector('video[data-reel="true"], .video-reel');
        if (!isReelVideo) return;

        if (!(v as any).__scReelAutoBound) {
          (v as any).__scReelAutoBound = 1;
          const isFirstReel = section === firstReelSection;
          // Before starting this reel, stop any other media to avoid autoplay races.
          try { pauseAllOtherMedia(v); } catch {}
          void initReelAutoplay(v, section, isFirstReel);
          // Nudge: if autoplay was blocked/paused, poke it once after a short delay.
          setTimeout(() => {
            try {
              if (v.paused && !v.ended) {
                v.play().catch(() => {});
              }
            } catch {}
          }, 300);
        }
      });
    } else {
      dlog("playSectionVideos");
      playSectionVideos(section);
      if (!isNarrationSection) {
        try { enforceBackgroundAudioMix(section); } catch {}
      }
    }
  } catch {}
  dlog("watchForLateVideos");
  watchForLateVideos(section);

  // Background ignition (slight delay to let layout settle)
  if (section.dataset.bgIgnited !== "1") {
    setTimeout(() => {
      try { dlog("ignite-bg"); scheduleBackgroundIgnitionFor(section); } catch {}
    }, 300);
  }

}

function onLeaveSection(section: HTMLElement) {
  const idx = section.dataset.sectionIndex || "unknown";
  log(`[OBSERVER] Deactivating section ${idx}`);
  dlog("onLeave", { idx, time: Date.now() });
  section.classList.remove("visible");

  // Stop queued reveals / timers / MOs
  try { cancelSectionAnimations(section); } catch {}
  cleanupReelBindings(section);
  try {
    const trackedEntry = tracked.get(section);
    if (trackedEntry?.narrDelayTimeout) {
      clearTimeout(trackedEntry.narrDelayTimeout);
      trackedEntry.narrDelayTimeout = null;
    }
  } catch {}
  try {
    const t = tracked.get(section);
    if (t && (t as any).onReelEnd) {
      document.removeEventListener("sc:reel:end", (t as any).onReelEnd as EventListener, { capture: false } as any);
      (t as any).onReelEnd = null;
    }
  } catch {}

  // Stop media for THIS section only (skip reels to avoid autoplay race; next activation will stop it)
  const isReel = section.dataset.hudType === "reel";
  if (!isReel) {
    dlog("pauseMediaInSection (leave)");
    pauseMediaInSection(section);
    dlog("pauseSectionVideos");
    try { pauseSectionVideos(section); } catch {}
  } else {
    dlog("skip pause on reel leave; next activation will stop prior reel");
  }

  // Pause background motion
  dlog("pause-bg");
  try { pauseBackgroundFor(section); } catch {}
}

export function observeSections() {
  normalizeScrollWrapper();

  const scroller = document.getElementById("scroll-wrapper") || undefined;
  const sections = document.querySelectorAll<HTMLElement>("section[data-section-index]");
  if (!sections.length) {
    console.debug("[OBSERVER] No sections to observe");
    return;
  }
  log("[OBSERVER] Observing existing sections (GSAP)…");
  dlog("observer-start", {
    sections: Array.from(document.querySelectorAll("section[data-section-index]")).length,
    vis: document.visibilityState,
  });

  // Kill prior triggers we created
  ScrollTrigger.getAll().forEach((st) => {
    if ((st as any)._scObserverTag === "section-visibility") st.kill();
  });

  sections.forEach((section) => {
    const st = ScrollTrigger.create({
      trigger: section,
      scroller: scroller || undefined,
      start: "top 65%",
      end: "bottom 35%",
      invalidateOnRefresh: true,
      onEnter: () => onEnterSection(section),
      onEnterBack: () => onEnterSection(section),
      onLeave: () => onLeaveSection(section),
      onLeaveBack: () => onLeaveSection(section),
    });
    (st as any)._scObserverTag = "section-visibility";
  });

  requestAnimationFrame(() => ScrollTrigger.refresh());
  window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && lastSection && lastSection.classList.contains("visible")) {
      dlog("vis-visible-retry", { idx: lastSection.dataset.sectionIndex });
      try { playSectionVideos(lastSection); } catch {}
      setTimeout(() => {
        try {
          const vids = lastSection!.querySelectorAll<HTMLVideoElement>(VIDEO_SELECTOR);
          vids.forEach((v) => {
            const isBg = v.dataset.role === "background" || v.dataset.bg === "true";
            const authoredNoLoop = v.dataset.loop === "false" || !v.loop;
            const finished = v.ended || v.dataset.bgFinished === "1";
            if (isBg && authoredNoLoop && finished) return;

            // don't restart finished play-once reels
            const isReel = v.dataset.reel === "true" || v.dataset.role === "reel";
            const reelOnce = v.dataset.reelPlayOnce === "true";
            const reelFinished = v.ended || v.dataset.reelPlayed === "1";
            if (isReel && reelOnce && reelFinished) return;

            if (v.paused) v.play().catch(() => {});
          });
        } catch {}
      }, 100);
      try { if (lastSection) enforceBackgroundAudioMix(lastSection as HTMLElement); } catch {}
    }
  });

  try { initArrowNavigation(); } catch {}
}

export function teardownSections() {
  ScrollTrigger.getAll().forEach((st) => {
    if ((st as any)._scObserverTag === "section-visibility") st.kill();
  });
  lastSection = null;
  firstReelSection = null;
}
function isNarrationGloballyMuted(): boolean {
  try {
    return AudioController.isMuted();
  } catch {
    try {
      return (window as any).__storycore_narration_muted === true;
    } catch {
      return false;
    }
  }
}
